import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { useToast } from '../../contexts/ToastContext';
import fuelPumpPricingService, {
  getStaleFileDetail,
  PriceFileRow,
  PumpSiteRow,
  RegionOption,
} from '../../services/fuelPumpPricing.service';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

/**
 * Fuel Pump Pricing — /data-management/fuel-pump-pricing.
 *
 * Two tabs over the Pilot "Better Of" daily price feed:
 *   - Pump Sites: every site with current pricing and its driver-region tags.
 *     Only ACTIVE sites TAGGED to a region ever show to that region's drivers
 *     in gwimove — untagged sites are invisible, and a site that drops out of
 *     the newest sheet is auto-deactivated (tags survive, so a returning site
 *     lights back up).
 *   - File History: every ingested sheet (kept in Azure blob), downloadable,
 *     with a purge-older-than control.
 *
 * Ingestion is the Upload button for now; the scheduled FTP pull will feed the
 * same import once credentials exist.
 */

/** Pump prices print with 3-4 decimals (6.459, 5.6157); blank when unknown. */
const fmtPrice = (v: unknown): string => {
  if (typeof v !== 'number') return '';
  const s = v.toFixed(4);
  return s.endsWith('0') ? s.slice(0, -1) : s;
};

const priceCell = (info: { getValue: () => unknown }) => (
  <span className="block text-right tabular-nums">{fmtPrice(info.getValue())}</span>
);

/** ISO yyyy-mm-dd → MM/DD/YYYY without a timezone round-trip. */
const fmtDate = (iso: string | null): string => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}/${d}/${y}`;
};

const fmtDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '';

const FuelPumpPricing: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab] = useState<'sites' | 'files'>('sites');
  useFavoriteTab(tab, setTab, ['sites', 'files']);

  const [sites, setSites] = useState<PumpSiteRow[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState<string | null>(null);

  const [files, setFiles] = useState<PriceFileRow[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [filesError, setFilesError] = useState<string | null>(null);

  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [editingSite, setEditingSite] = useState<PumpSiteRow | null>(null);
  const [editingIds, setEditingIds] = useState<number[]>([]);
  const [savingRegions, setSavingRegions] = useState(false);

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [purgeDate, setPurgeDate] = useState('');
  const [purging, setPurging] = useState(false);

  const loadSites = useCallback(async (signal?: AbortSignal) => {
    setSitesLoading(true);
    setSitesError(null);
    try {
      setSites(await fuelPumpPricingService.getSites(signal));
    } catch (err) {
      if (signal?.aborted) return;
      console.error('Error loading pump sites:', err);
      setSitesError('Could not load pump sites.');
    } finally {
      if (!signal?.aborted) setSitesLoading(false);
    }
  }, []);

  const loadFiles = useCallback(async (signal?: AbortSignal) => {
    setFilesLoading(true);
    setFilesError(null);
    try {
      setFiles(await fuelPumpPricingService.getFiles(signal));
    } catch (err) {
      if (signal?.aborted) return;
      console.error('Error loading price files:', err);
      setFilesError('Could not load file history.');
    } finally {
      if (!signal?.aborted) setFilesLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadSites(controller.signal);
    loadFiles(controller.signal);
    fuelPumpPricingService
      .getRegions(controller.signal)
      .then(setRegions)
      .catch((err) => {
        if (!controller.signal.aborted) console.error('Error loading regions:', err);
      });
    return () => controller.abort();
  }, [loadSites, loadFiles]);

  const openRegionEditor = useCallback((row: PumpSiteRow) => {
    setEditingSite(row);
    setEditingIds(row.regions.map((r) => r.id));
  }, []);

  const saveRegions = async () => {
    if (!editingSite) return;
    setSavingRegions(true);
    try {
      await fuelPumpPricingService.setSiteRegions(editingSite.recno, editingIds);
      const byId = new Map(regions.map((r) => [r.id, r]));
      const newTags = editingIds
        .map((id) => byId.get(id))
        .filter((r): r is RegionOption => !!r)
        .sort((a, b) => a.name.localeCompare(b.name));
      setSites((prev) =>
        prev.map((s) => (s.recno === editingSite.recno ? { ...s, regions: newTags } : s))
      );
      showToast({ message: `Regions updated for site ${editingSite.site}.`, type: 'success' });
      setEditingSite(null);
    } catch (err) {
      console.error('Error saving site regions:', err);
      showToast({ message: 'Could not save regions.', type: 'error' });
    } finally {
      setSavingRegions(false);
    }
  };

  const onUploadPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    try {
      let result;
      try {
        result = await fuelPumpPricingService.uploadFile(file);
      } catch (err) {
        // The sheet isn't newer than what's loaded — importing it would roll
        // every price backwards, so confirm before forcing it through.
        const stale = getStaleFileDetail(err);
        if (!stale) throw err;
        const loaded = fmtDate(stale.loadedEffectiveDate);
        const incoming = stale.incomingEffectiveDate
          ? `dated ${fmtDate(stale.incomingEffectiveDate)}`
          : 'has no effective date';
        if (
          !window.confirm(
            `${file.name} ${incoming}, but prices effective ${loaded} are already loaded.\n\n` +
              'Importing it will replace current prices with older ones. Import anyway?'
          )
        ) {
          return;
        }
        result = await fuelPumpPricingService.uploadFile(file, true);
      }
      showToast({
        message:
          `Imported ${result.rows} sites from ${file.name}: ${result.inserted} new, ` +
          `${result.updated} updated, ${result.deactivated} deactivated.`,
        type: result.stale ? 'warning' : 'success',
      });
      result.warnings.forEach((w) => showToast({ message: w, type: 'warning' }));
      await Promise.all([loadSites(), loadFiles()]);
    } catch (err: any) {
      console.error('Error uploading price sheet:', err);
      const detail = err?.response?.data?.detail;
      showToast({
        message: typeof detail === 'string' ? detail : 'Upload failed.',
        type: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const onPurge = async () => {
    if (!purgeDate) return;
    if (
      !window.confirm(
        `Delete all archived price files uploaded before ${fmtDate(purgeDate)}? ` +
          'The spreadsheets are removed from storage as well.'
      )
    ) {
      return;
    }
    setPurging(true);
    try {
      const deleted = await fuelPumpPricingService.purgeFiles(purgeDate);
      showToast({
        message: deleted
          ? `Deleted ${deleted} archived file${deleted === 1 ? '' : 's'}.`
          : 'No files older than that date.',
        type: deleted ? 'success' : 'info',
      });
      if (deleted) await loadFiles();
    } catch (err) {
      console.error('Error purging price files:', err);
      showToast({ message: 'Could not delete old files.', type: 'error' });
    } finally {
      setPurging(false);
    }
  };

  const siteColumns = useMemo<ColumnDef<PumpSiteRow>[]>(
    () => [
      { accessorKey: 'site', header: 'Site', enableSorting: true },
      { accessorKey: 'city', header: 'City', enableSorting: true },
      { accessorKey: 'st', header: 'ST', enableSorting: true },
      {
        accessorKey: 'retailPrice',
        header: 'Retail Price',
        enableSorting: true,
        cell: priceCell,
      },
      {
        accessorKey: 'yourPrice',
        header: 'Your Price',
        enableSorting: true,
        cell: priceCell,
      },
      {
        accessorKey: 'savingsTotal',
        header: 'Savings',
        enableSorting: true,
        cell: priceCell,
      },
      {
        id: 'regions',
        header: 'Regions',
        // Names in the accessor so global search + column filter work on them.
        accessorFn: (row) => row.regions.map((r) => r.name).join(', '),
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          return (
            <button
              type="button"
              onClick={() => openRegionEditor(row)}
              className="group flex flex-wrap items-center gap-1 text-left"
              title="Edit regions"
            >
              {row.regions.length === 0 ? (
                <span className="text-xs italic text-gray-400 dark:text-gray-500">
                  untagged — hidden from drivers
                </span>
              ) : (
                row.regions.map((r) => (
                  <span
                    key={r.id}
                    className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                  >
                    {r.name}
                  </span>
                ))
              )}
              <PencilSquareIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100" />
            </button>
          );
        },
      },
      {
        // Which day's prices these are — distinct from Last Updated (when we
        // last ingested a file containing this site), so a forced import of an
        // older sheet is visible rather than hiding behind a fresh timestamp.
        accessorKey: 'priceEffectiveDate',
        header: 'Prices Effective',
        enableSorting: true,
        cell: (info) => fmtDate(info.row.original.priceEffectiveDate),
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Last Updated',
        enableSorting: true,
        cell: (info) => fmtDateTime(info.row.original.lastUpdated),
      },
      {
        id: 'active',
        header: 'Status',
        accessorFn: (row) => (row.active ? 'Active' : 'Inactive'),
        enableSorting: true,
        cell: (info) =>
          info.row.original.active ? (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-900/40 dark:text-green-200">
              Active
            </span>
          ) : (
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              Inactive
            </span>
          ),
      },
    ],
    [openRegionEditor]
  );

  const fileColumns = useMemo<ColumnDef<PriceFileRow>[]>(
    () => [
      {
        accessorKey: 'uploadedAt',
        header: 'Uploaded',
        enableSorting: true,
        cell: (info) => fmtDateTime(info.row.original.uploadedAt),
      },
      { accessorKey: 'filename', header: 'File', enableSorting: true },
      { accessorKey: 'source', header: 'Source', enableSorting: true },
      { accessorKey: 'account', header: 'Account', enableSorting: true },
      {
        accessorKey: 'priceEffectiveDate',
        header: 'Effective',
        enableSorting: true,
        cell: (info) => fmtDate(info.row.original.priceEffectiveDate),
      },
      {
        accessorKey: 'retailPriceAsOf',
        header: 'Retail As Of',
        enableSorting: true,
        cell: (info) => fmtDateTime(info.row.original.retailPriceAsOf),
      },
      { accessorKey: 'rowCount', header: 'Rows', enableSorting: true },
      { accessorKey: 'insertedCount', header: 'New', enableSorting: true },
      { accessorKey: 'updatedCount', header: 'Updated', enableSorting: true },
      { accessorKey: 'deactivatedCount', header: 'Deactivated', enableSorting: true },
      { accessorKey: 'uploadedBy', header: 'By', enableSorting: true },
      {
        id: 'download',
        header: '',
        enableSorting: false,
        cell: (info) => (
          <button
            type="button"
            onClick={() =>
              fuelPumpPricingService.downloadFile(info.row.original).catch((err) => {
                console.error('Error downloading price file:', err);
                showToast({ message: 'Could not download the file.', type: 'error' });
              })
            }
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            title={`Download ${info.row.original.filename}`}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span className="text-xs">Download</span>
          </button>
        ),
      },
    ],
    [showToast]
  );

  const tabButton = (key: 'sites' | 'files', label: string) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`rounded-t-lg border-b-2 px-4 py-2 text-sm font-medium ${
        tab === key
          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-body">Fuel Pump Pricing</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Pilot truck-stop pricing from the daily &ldquo;Better Of&rdquo; sheet. Drivers only
            see <span className="font-medium">active</span> sites tagged to their region in
            gwiMove — untagged sites are hidden.
          </p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            className="hidden"
            onChange={onUploadPicked}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            {uploading ? 'Importing…' : 'Upload Price Sheet'}
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {tabButton('sites', `Pump Sites (${sites.length})`)}
        {tabButton('files', `File History (${files.length})`)}
      </div>

      {tab === 'sites' ? (
        <TanTable
          data={sites}
          columns={siteColumns}
          loading={sitesLoading}
          errorMessage={sitesError}
          onRetry={() => loadSites()}
          settingsKey="fuel-pump-sites-table"
          fieldName="fuel-pump-sites-table"
          cardTitle="Pump Sites"
          searchPlaceholder="Search sites, cities, regions..."
          defaultSorting={[{ id: 'site', desc: false }]}
          enableGrouping={false}
          enableColumnFilters={true}
        />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <label
              htmlFor="purge-before"
              className="text-sm text-gray-700 dark:text-gray-300"
            >
              Remove archived files uploaded before
            </label>
            <input
              id="purge-before"
              type="date"
              value={purgeDate}
              onChange={(e) => setPurgeDate(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800"
            />
            <button
              type="button"
              disabled={!purgeDate || purging}
              onClick={onPurge}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {purging ? 'Deleting…' : 'Delete Old Files'}
            </button>
          </div>
          <TanTable
            data={files}
            columns={fileColumns}
            loading={filesLoading}
            errorMessage={filesError}
            onRetry={() => loadFiles()}
            settingsKey="fuel-price-files-table"
            fieldName="fuel-price-files-table"
            cardTitle="File History"
            searchPlaceholder="Search files..."
            defaultSorting={[{ id: 'uploadedAt', desc: true }]}
            enableGrouping={false}
            enableColumnFilters={true}
          />
        </div>
      )}

      {editingSite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-body">
              Regions — Site {editingSite.site} ({editingSite.city}, {editingSite.st})
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Drivers in the checked regions will see this pump in gwiMove.
            </p>
            <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
              {regions.map((r) => (
                <label
                  key={r.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={editingIds.includes(r.id)}
                    onChange={(e) =>
                      setEditingIds((prev) =>
                        e.target.checked
                          ? [...prev, r.id]
                          : prev.filter((id) => id !== r.id)
                      )
                    }
                  />
                  <span className="text-sm text-body">{r.name}</span>
                </label>
              ))}
              {regions.length === 0 && (
                <p className="text-sm italic text-gray-500">No regions found.</p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingSite(null)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={savingRegions}
                onClick={saveRegions}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingRegions ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelPumpPricing;
